// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../utils/gql.ts'
import { ComandaFragmentAst } from '../fragments/ComandaFragment.ts'
import type { ComandaProps } from '../interfaces/comanda.ts'

const query = /* GraphQL */ gqlAst`
  ${ComandaFragmentAst}

  query COMANDAS_COCINA($codigoSucursal: Int!, $areas: [String!]!) {
    restCocinaComandas(codigoSucursal: $codigoSucursal, areas: $areas) {
      version
      comandas {
        ...comandasFields
      }
    }
  }
`

export const KEY_COMANDAS_COCINA = 'comandas-cocina'

export interface ApiComandasCocinaParams {
  codigoSucursal: number
  /** _id de las áreas (Area); impresora si el artículo no tiene áreas; o GENERAL. [] = todas las áreas */
  areas: string[]
}

export interface ComandasCocinaProps {
  /** Versión del listado; cambia cuando alguna comanda de las áreas cambia */
  version: number
  comandas: ComandaProps[]
}

/**
 * Comandas con alguna de las áreas pedidas abierta (PENDIENTE, EN_PREPARACION, LISTO), por orden de llegada.
 * Las líneas de cada área se filtran en el cliente por lineas.areasImpresion.
 * @param params Sucursal y áreas a consultar.
 * @param options Headers adicionales y señal de cancelación de la petición.
 */
export const apiComandasCocina = async (
  params: ApiComandasCocinaParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ComandasCocinaProps> => {
  try {
    const data = await graphqlClient.request<{ restCocinaComandas: ComandasCocinaProps }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    console.log('api comandas version', data.restCocinaComandas.version)
    return data.restCocinaComandas
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida las comandas de cocina desde cualquier lugar (ej. al cambiar el estado de una comanda),
 * sin necesidad de tener montado el hook.
 * @example invalidateComandasCocina()
 * @example invalidateComandasCocina({ extraQueryKeys: [codigoSucursal] })
 */
export const invalidateComandasCocina = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_COMANDAS_COCINA, options)

/**
 * Hook para consultar las comandas abiertas de cocina de una o varias áreas.
 * queryKey: [KEY_COMANDAS_COCINA, codigoSucursal, area, ...(options.queryKeys ?? [])]
 * Para consultar periódicamente, pasar `refetchInterval` en options.
 * Para invalidar sin montar el hook, usar `invalidateComandasCocina()`.
 * @param params Sucursal y áreas a consultar.
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 */
export const useComandasCocina = (
  params: ApiComandasCocinaParams,
  options: QueryOpts<ComandasCocinaProps> = {},
) =>
  useApiQuery(KEY_COMANDAS_COCINA, (ctx) => apiComandasCocina(params, { signal: ctx.signal }), {
    ...options,
    enabled: options.enabled ?? true,
    queryKeys: [params.codigoSucursal, params.areas, ...(options.queryKeys ?? [])],
  })
